/**
 * The planner's structured preferences, and the pure edits the panel makes.
 *
 * Two levels: `global` states each rule, `days[weekday]` overrides it. **An
 * absent key inherits and an empty value overrides**, and keeping those apart
 * is the whole reason these helpers exist rather than the panel writing the
 * object inline: for `double_features`, omitting the key takes the global's
 * while setting one replaces it. Writing a value where the panel meant
 * "inherit" silently pins a rule, and writing `undefined` where it meant
 * "set" silently drops one.
 *
 * `windows` and `preferred_start` are the exception: they are per-day ONLY,
 * with no global to inherit from, so for those an absent key simply means no
 * restriction.
 */

export type Tier = string[];

export type DayRules = {
  enabled?: boolean;
  windows?: Array<[number, number]>;
  /** Theater ids this day may use. Empty or absent means any, exactly like
   *  `windows`. A RESTRICTION, not a ranking: `global.theater_tiers` still
   *  decides which theater a film is worth seeing at, once, across the whole
   *  horizon. This only narrows what a given weekday offers, alongside its
   *  hours -- which is what lets "Tuesdays near work" coexist with "never
   *  drive further than you have to". */
  theaters?: string[];
  preferred_start?: number | null;
  double_features?: DoubleFeatureMode;
  /** Films this day may hold. The A-List allowance is weekly, so without this
   *  one day could take a whole week's slots. */
  max_films?: number;
  /** Most dead time, in minutes, between the halves of a double feature. NOT
   *  the transfer buffer between two unpaired films, which is fixed. */
  double_feature_gap?: number;
};

export type DoubleFeatureMode = "off" | "preferred" | "required";

/** Rules that have no per-day form. Note this does NOT extend `DayRules`:
 *  hours and preferred start are per-day only, and advertising them here is
 *  what let stale reads of `global.windows` typecheck cleanly. */
export type GlobalRules = {
  double_features?: DoubleFeatureMode;
  max_films?: number;
  double_feature_gap?: number;
  /** Best tier first; names inside one tier are equally good. This RANKING is
   *  global on purpose -- "only go further out when the closer theater has
   *  nothing that fits" is one statement about the whole horizon, and a
   *  per-day version of it made each weekday its own comparison group, which
   *  broke it. `DayRules.theaters` is a restriction rather than a ranking and
   *  composes with this instead of competing. */
  theater_tiers?: Tier[];
  preferred_days?: number[];
  exclude_formats?: string[];
  include_discovery_matches?: boolean;
  priorities?: string[];
};

export type PlannerPreferences = {
  _version?: number;
  automatic_enabled: boolean;
  horizon_weeks?: number;
  global: GlobalRules;
  days: Record<string, DayRules>;
  calendar: {
    blocked_dates?: string[];
    blocked_windows?: Record<string, Array<[number, number]>>;
  };
};

export type PlannerOptions = {
  theaters: Array<{ id: string; label: string }>;
  priority_factors: Array<{ id: string; label: string }>;
};

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const DEFAULT_PREFERENCES: PlannerPreferences = {
  automatic_enabled: true,
  global: {},
  days: {},
  calendar: {},
};

/** `840` -> `"14:00"`, for an `<input type="time">`. */
export function toClock(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

/** `"14:00"` -> `840`. NaN-safe: an empty input reads as null, not as midnight. */
export function toMinutes(value: string): number | null {
  const [hours, minutes] = value.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
}

/**
 * Merge a patch, dropping any key the patch set back to "inherit".
 *
 * `undefined` is the panel's inherit signal and must not survive into the
 * stored object: a present-but-undefined key round-trips through JSON as an
 * absent one anyway, so deleting it keeps the two representations identical and
 * a diff of the blob says what was actually set.
 */
function mergeRules<T extends object>(current: T, patch: Partial<T>): T {
  const merged = { ...current, ...patch };
  (Object.keys(patch) as Array<keyof T>).forEach((key) => {
    if (patch[key] === undefined) delete merged[key];
  });
  return merged;
}

/** Merge one weekday's overrides. */
export function setDay(
  preferences: PlannerPreferences,
  weekday: number,
  patch: DayRules,
): PlannerPreferences {
  const merged = mergeRules(preferences.days[String(weekday)] ?? {}, patch);
  return { ...preferences, days: { ...preferences.days, [String(weekday)]: merged } };
}

/** Merge global rules. */
export function setGlobal(
  preferences: PlannerPreferences,
  patch: GlobalRules,
): PlannerPreferences {
  return { ...preferences, global: mergeRules(preferences.global, patch) };
}

/** Toggle one value in a list-valued rule, keeping the order stable. */
export function toggle<T>(values: T[] | undefined, value: T): T[] {
  // `?? []` is not enough: it fires on null/undefined only, so a stored value
  // of the WRONG TYPE reaches the body intact. A string is the reachable case
  // -- the blob is JSON off GCS, handed back verbatim by
  // `GET /api/planner/preferences` and consumed here through a bare
  // `as Promise<StoredPlannerPreferences>` assertion, which checks nothing at
  // runtime. `"mission_valley".includes(...)` is a SUBSTRING test that happily
  // returns true, and then `.filter` does not exist on a string and throws; the
  // other branch spreads the string into one entry per character and saves that
  // back. `services/planner_rules._drop_unusable` refuses to store such a value,
  // but it cannot reach one already stored, hand-edited, or written by anything
  // that does not route through `sanitize`. This is the layer that can.
  const current = Array.isArray(values) ? values : [];
  return current.includes(value)
    ? current.filter((entry) => entry !== value)
    : [...current, value];
}

/**
 * Theater ids as a flat list plus a "ties with the one above" flag per entry.
 *
 * Tiers are a list of lists, which no ordinary control edits well. Flattening
 * to (id, tied) makes the panel a reorderable list with one checkbox, and
 * `toTiers` puts it back. A tier list is PARTIAL by design -- the server
 * appends every theater the list does not name, in city order -- so this only
 * has to describe the ones the user ranked.
 */
export function fromTiers(tiers: Tier[] | undefined): Array<{ id: string; tied: boolean }> {
  return (tiers ?? []).flatMap((tier, tierIndex) =>
    tier.map((id, index) => ({ id, tied: tierIndex > 0 && index === 0 ? false : index > 0 })),
  );
}

export function toTiers(entries: Array<{ id: string; tied: boolean }>): Tier[] {
  const tiers: Tier[] = [];
  entries.forEach((entry) => {
    if (entry.tied && tiers.length > 0) tiers[tiers.length - 1].push(entry.id);
    else tiers.push([entry.id]);
  });
  return tiers;
}

/** Move an entry within a list. Out-of-range moves are a no-op, not a throw. */
export function move<T>(values: T[], from: number, to: number): T[] {
  if (from < 0 || to < 0 || from >= values.length || to >= values.length) return values;
  const next = [...values];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/** A one-line summary of a day, for the collapsed card header. */
export function describeDay(
  preferences: PlannerPreferences,
  weekday: number,
): string {
  const day = preferences.days[String(weekday)] ?? {};
  if (day.enabled === false) return "Never";
  const parts: string[] = [];
  // Per-day ONLY: the server stopped reading `global.windows` and
  // `global.preferred_start`, so falling back to them here described hours
  // that no longer apply -- a stored global restriction would still be shown
  // as a day's effective hours while the solver ignored it.
  const windows = day.windows;
  if (windows && windows.length > 0) {
    parts.push(windows.map(([from, to]) => `${toClock(from)}–${toClock(to)}`).join(", "));
  } else {
    parts.push("Any time");
  }
  const theaters = day.theaters ?? [];
  if (theaters.length > 0) parts.push(`${theaters.length} theater${theaters.length > 1 ? "s" : ""} only`);
  const start = day.preferred_start;
  if (start !== undefined && start !== null) parts.push(`around ${toClock(start)}`);
  const doubles = day.double_features ?? preferences.global.double_features;
  if (doubles === "required") parts.push("double features only");
  if (doubles === "off") parts.push("no double features");
  return parts.join(" · ");
}
